"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.cloneLogger = cloneLogger;
exports.errorTypeCtors = void 0;
exports.isErrorType = isErrorType;
exports.messageToJson = messageToJson;
exports.messageToJsonObject = messageToJsonObject;
exports.voidLogFunction = void 0;
exports.wrapLogger = wrapLogger;
exports.wrapLoggerWithFlags = wrapLoggerWithFlags;
var _lodash = require("@avantia/lodash");
var _customLoggers = require("./customLoggers");
var _HpError = require("./HpError");
var _serializingUtilities = require("./serializingUtilities");
const voidLogFunction = () => {
  return false;
};

/**
 * Clones the provided logger.
 * @param logger the logger to clone.
 * @param ignore a map of logger method names. Provide true to ignore, false to leave as is.
 * @returns a cloned logger.
 */
exports.voidLogFunction = voidLogFunction;
function cloneLogger(logger, ignore) {
  const {
    debug,
    info,
    notice,
    warn,
    error,
    critical
  } = logger;
  const res = {
    debug,
    info,
    notice,
    warn,
    error,
    critical
  };
  if (ignore) {
    _lodash.lodash.keys(ignore).forEach(keyString => {
      const key = keyString;
      if (ignore[key] === 'ignore') {
        res[key] = voidLogFunction;
      }
    });
  }
  return res;
}

/**
 * Wraps the provided logger (or the default console if not is provided), using the 'modifer'
 * function to pipe the result to the underlying logger.
 * @param modifier the modifier function.
 * @param logger the logger to wrap (or the basic console logger if none is provided).
 * @returns a new logger that calls 'modifier' against each log argument, and logs the result.
 */
function wrapLogger(modifier, logger) {
  const {
    debug,
    info,
    notice,
    warn,
    error,
    critical
  } = logger || (0, _customLoggers.createBasicConsoleLogger)();
  return {
    debug: s => debug(modifier(s)),
    info: s => info(modifier(s)),
    notice: s => notice(modifier(s)),
    warn: s => warn(modifier(s)),
    error: s => error(modifier(s)),
    critical: s => critical(modifier(s))
  };
}

/**
 * Wraps the provided logger, using the 'modifer' function to pipe the result to the underlying logger.
 * @param modifier the modifier function.
 * @param logger the logger to wrap.
 * @returns a new logger that calls 'modifier' against each log argument, and logs the result.
 */
function wrapLoggerWithFlags(modifier, logger) {
  const {
    debug,
    info,
    notice,
    warn,
    error,
    critical
  } = logger;
  return {
    debug: (s, f) => debug(modifier(s), f),
    info: (s, f) => info(modifier(s), f),
    notice: (s, f) => notice(modifier(s), f),
    warn: (s, f) => warn(modifier(s), f),
    error: s => error(modifier(s)),
    critical: s => critical(modifier(s))
  };
}

/**
 * This method produces a Message object that, when stringified, is suitable to be output for DataDog or the console.
 * @returns a Message object.
 */
function messageToJsonObject({
  message,
  level,
  stackTrace = true
}) {
  const basicData = {
    timestamp: new Date().toISOString(),
    level
  };
  const data = basicData;
  const messageType = typeof message;
  if (messageType === 'string') {
    data.message = message;
  } else if (messageType === 'function') {
    data.message = message();
  } else {
    const hpErr = message;
    let content;
    if (hpErr.isHpError) {
      content = (0, _HpError.errorToJsonObject)(hpErr, {
        stackTrace
      });
    } else if (isErrorType(message)) {
      content = (0, _HpError.errorToJsonObject)(new _HpError.HpError(message), {
        stackTrace
      });
    } else {
      const {
        error,
        result
      } = (0, _serializingUtilities.ensureDataIsSerializable)(message);
      if (error) {
        content = (0, _HpError.errorToJsonObject)(error, {
          stackTrace
        });
      } else {
        content = result;
      }
    }
    if (!(0, _serializingUtilities.hasOwnProperty)(content, 'level') && !(0, _serializingUtilities.hasOwnProperty)(content, 'timestamp')) {
      if (_lodash.lodash.keys(content).length > 0) {
        Object.assign(data, content);
      } else {
        data.message = '' + message;
      }
    } else {
      data.message = content;
    }
  }
  return data;
}

/**
 * This method produces a string that is suitable to be output for DataDog or the console.
 * @returns a JSON string
 */
function messageToJson({
  message,
  level,
  formatted,
  sensitiveDetailMask
}) {
  const data = messageToJsonObject({
    message,
    level
  });
  let json = JSON.stringify(data, null, formatted ? 2 : undefined);
  if (sensitiveDetailMask) {
    json = sensitiveDetailMask(json);
  }
  return json;
}
const objectCtor = {}.constructor;
const errorTypeCtors = Object.freeze([new Error(), new EvalError(), new SyntaxError(), new RangeError(), new ReferenceError(), new TypeError(), new URIError()].map(err => err.constructor));
exports.errorTypeCtors = errorTypeCtors;
function isErrorType(value) {
  const ctor = value ? value.constructor : undefined;
  return !!ctor && ctor !== objectCtor && (errorTypeCtors.indexOf(ctor) >= 0 || looksLikeAnError(value));
}
function looksLikeAnError(error) {
  const {
    name,
    message
  } = error;
  return !!name && name.endsWith('Error') && !!message;
}

// eslint-disable-next-line @typescript-eslint/ban-types